import { useMutation, useQueryClient } from 'react-query';
import { StatusResponse } from '../../../../../../../types/API';
import { wgApi } from '../../../../../../app/src/features/api/wgApi';
import { getUseAdvisoryReportQKey } from '../../../../api/getAdvisoryReport';
import {
  AdvisoryReport,
  InsurancePolicyType,
} from '../../../../types/AdvisoryReport';

interface Params {
  reportId: string;
  insuranceId: string;
  type: InsurancePolicyType;
}

export const deleteInsurancePolicyById = async ({
  reportId,
  insuranceId,
}: Omit<Params, 'type'>): Promise<StatusResponse> => {
  const { data, isError } = await wgApi.delete<StatusResponse>(
    `v1/api/advisory-reports/${reportId}/insurance-policies/${insuranceId}`,
  );
  if (isError) throw new Error();
  return data;
};

export const useDeleteInsurancePolicy = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ type, ...params }: Params) => deleteInsurancePolicyById(params),
    {
      onMutate: async ({ insuranceId, reportId, type }) => {
        const qKey = getUseAdvisoryReportQKey(reportId);
        await queryClient.cancelQueries(qKey);

        const previousReport = queryClient.getQueryData<AdvisoryReport>(qKey);
        const policyType =
          type === 'CURRENT-INSURANCE' ? 'current_insurances' : 'offers';
        if (previousReport) {
          queryClient.setQueryData(qKey, {
            ...previousReport,
            insurance_policies: {
              ...previousReport.insurance_policies,
              [policyType]: previousReport.insurance_policies[
                policyType
              ].filter(({ id }) => id !== insuranceId),
            },
          });
        }
        return { qKey, previousReport };
      },
      onError: (_, __, context) => {
        if (context?.previousReport) {
          queryClient.setQueryData(context.qKey, context.previousReport);
        }
      },
    },
  );
};
